import { api, SERVER } from "../scripts/api.js";

/*
   <app-skill-map> - the program's skill map (2026-10-19).
   Routes: #skill-map               skills by area: scope, per-position importance and target tier
           #skill-map/gaps          practice to build (required skills with no tab, or none at target)
           #skill-map/cases         cases broken into skills
           #skill-map/case/<slug>   one case: where each skill sits on the map, coverage, the team
           #skill-map/requests      the coaches' requests for more practice (2026-10-20): David answers here

   David, 10/19: "check each skill within each of the 10 categories and start assigning them to
   either positions or assigning them as foundational ... Foundational skills can have different
   levels and different importance for each position, but they should be tested for everyone."
   Design: Skill-Map-v2-DESIGN-2026-10-19.md (second brain, Excel-Esports-Team/Skills).
   Server: routes/skill-map.js. Admin edits; professors read.
*/

const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const POS = ["Point", "Molder", "Closer"];
const SCOPE_LABEL = { review: "To review", foundational: "Foundational", position: "Position" };
const REASON = {
   foundational_needs_targets: "Foundational needs a target tier for every position",
   position_needs_an_owner: "rate at least one position 2 or 3",
   position_needs_targets: "each position rated 2 or 3 needs a target tier",
   name_taken: "another skill already has that name",
   invalid_parent: "pick a function (not a usage) to put it under", usages_not_installed: "the usages migration has not run yet",
   invalid_area: "unknown area", invalid_scope: "unknown scope", invalid_targets: "importance 0-3, tier 1-5",
};
const PRI = { now: "Now", soon: "Soon", later: "Later" };     // David's priority per skill (2026-10-20); unset reads as soon
const KIND = { more_practice: "More practice", harder: "Harder tabs", new_skill: "New skill", other: "Other" };
const RQ = { open: "Open", building: "Being built", done: "Done", declined: "Declined" };
REASON.priorities_not_installed = "the coaching migration (priorities) has not run yet";
const reason = (r, d) => `${REASON[r] || r}${d ? ` (${d})` : ""}`;

class AppSkillMap extends HTMLElement {
   connectedCallback() {
      this.innerHTML = `
         <section id="skill-map">
            <div class="page-head">
               <h1>Skill map</h1>
               <p class="sub">Every skill the team trains, by area. <b>Foundational</b> skills are tested for every player (each position
                  can have its own importance and target tier); <b>Position</b> skills are tested only for the positions rated 2 or 3.
                  Tiers are the platform's tab tiers, 1 to 5.</p>
            </div>
            <div class="pr-tabs" role="group" aria-label="View">
               <a class="pr-tab" data-tab="skills" href="#skill-map">Skills</a>
               <a class="pr-tab" data-tab="gaps" href="#skill-map/gaps">Practice to build</a>
               <a class="pr-tab" data-tab="cases" href="#skill-map/cases">Cases</a>
               <a class="pr-tab" data-tab="requests" href="#skill-map/requests">Requests<span class="sm-badge" id="sm-rq-n" hidden></span></a>
            </div>
            <p class="msg" id="sm-msg"></p>
            <div id="sm-body"></div>
         </section>`;
      this.filter = { q: "", show: "all", pos: "" };
      this.open = new Set();                            // functions whose usage rows are showing
   }

   msg(t, kind = "") { const m = this.querySelector("#sm-msg"); m.textContent = t; m.className = `msg${kind ? " " + kind : ""}`; }

   async get(path) {
      const res = await api(path);
      const d = await res.json().catch(() => ({ ok: false, reason: `http_${res.status}` }));
      if (!d.ok) throw new Error(d.reason || "failed");
      return d;
   }

   async show(param) {
      const [tab, ...rest] = String(param || "").split("/");
      const arg = rest.join("/");                       // the router has already decoded the hash
      this.tab = tab === "gaps" ? "gaps" : tab === "cases" || tab === "case" ? "cases" : tab === "requests" ? "requests" : "skills";
      this.querySelectorAll(".pr-tab").forEach((a) => a.classList.toggle("on", a.dataset.tab === this.tab));
      const body = this.querySelector("#sm-body");
      body.innerHTML = `<p class="empty">Loading...</p>`;
      this.msg("");
      this.requestBadge();
      try {
         if (this.tab === "requests") return this.renderRequests((await this.get("/skill-map/requests")).requests);
         if (this.tab === "gaps") return this.renderGaps((await this.get("/skill-map/gaps")).gaps);
         if (tab === "case" && arg) return this.renderCase(arg);
         if (this.tab === "cases") return this.renderCases((await this.get("/skill-map/cases")).cases);
         this.map = await this.get("/skill-map");
         this.renderSkills();
      } catch (e) {
         body.innerHTML = "";
         this.msg(e.message === "skill_map_not_installed" ? "The skill map is not installed on the server yet (migration 2026-10-19_skill-map.sql)."
            : `Could not load (${e.message}).`, "err");
      }
   }

   /* How many coach requests are waiting (quiet: no badge if coaching is not installed). */
   async requestBadge() {
      try {
         const res = await api("/skill-map/requests?status=open");
         const d = await res.json();
         const b = this.querySelector("#sm-rq-n");
         if (!d.ok || !b) return;
         b.textContent = String(d.requests.length);
         b.hidden = !d.requests.length;
      } catch { /* offline: no badge */ }
   }

   /* ---------- Skills ---------- */
   renderSkills() {
      const m = this.map;
      const body = this.querySelector("#sm-body");
      const c = m.counts;
      body.innerHTML = `
         <div class="sm-bar">
            <input type="search" id="sm-q" placeholder="Search skills" value="${esc(this.filter.q)}">
            <select id="sm-show">
               ${[["all", `All (${c.total})`], ["review", `To review (${c.review})`], ["foundational", `Foundational (${c.foundational})`],
                  ["position", `Position (${c.position})`], ["now", "Priority: now"], ["no_test", "No tab yet"], ["retired", "Retired"]]
                  .map(([v, l]) => `<option value="${v}" ${this.filter.show === v ? "selected" : ""}>${l}</option>`).join("")}
            </select>
            <select id="sm-pos"><option value="">Any position</option>${POS.map((p) => `<option ${this.filter.pos === p ? "selected" : ""}>${p}</option>`).join("")}</select>
            <a class="sm-dl" href="${SERVER}/skill-map/export" title="The whole map as JSON: what a case breakdown reads">Download (JSON)</a>
         </div>
         <p class="sm-note">${m.can_edit ? "Change a row, then Save it. A skill stays <b>To review</b> (not tested) until you decide its scope." :
            "Read only: David edits the map."} <b>Priority</b> orders the players' gap lists in the Game Launcher (now, then soon, then later). <b>Tier</b> is the skill's difficulty (1 to 5), the same for every position. Importance per position: 3 goes deep, 2 uses regularly, 1 awareness (not tested), 0 not needed.
            <b>Tabs</b> counts library tabs that carry the skill at tiers 1 to 5.</p>
         ${m.can_edit ? this.addForm() : ""}
         ${m.untracked_tags.length ? `<p class="msg warn">Tab tags no skill claims: ${m.untracked_tags.map((t) => esc(t.tag)).join(", ")}. Add them or list them as evidence.</p>` : ""}
         <div id="sm-areas"></div>`;
      const q = body.querySelector("#sm-q");
      q.addEventListener("input", () => { this.filter.q = q.value; this.drawAreas(); });
      body.querySelector("#sm-show").addEventListener("change", (e) => { this.filter.show = e.target.value; this.drawAreas(); });
      body.querySelector("#sm-pos").addEventListener("change", (e) => { this.filter.pos = e.target.value; this.drawAreas(); });
      if (m.can_edit) this.wireAdd();
      this.drawAreas();
   }

   addForm() {
      return `
         <details class="sm-add"><summary>Add a skill</summary>
            <div class="sm-add-row">
               <input type="text" id="sm-add-name" maxlength="80" placeholder="Name (e.g. MMULT, or Carry state down a list)">
               <select id="sm-add-kind">${["Function", "Usage", "Technique", "Concept", "Team skill"].map((k) => `<option>${k}</option>`).join("")}</select>
               <select id="sm-add-parent" title="For a usage: the function it belongs to"><option value="">Under function (for a usage)...</option>${
                  this.map.skills.filter((x) => x.kind === "Function" && !x.parent_id).sort((a, b) => a.name.localeCompare(b.name))
                     .map((x) => `<option value="${x.skill_id}">${esc(x.name)}</option>`).join("")}</select>
               <select id="sm-add-area"><option value="">Area...</option>${this.map.areas.map((a) => `<option>${esc(a)}</option>`).join("")}</select>
               <button type="button" id="sm-add-go">Add</button><span class="msg" id="sm-add-msg"></span>
            </div>
            <p class="sm-note">A function's evidence is its own name (the tab tag). A usage or technique has no evidence until tabs are tagged with it
               (the usage tagger reads each tab's worked formula; re-run it after adding a usage to its catalog).</p>
         </details>`;
   }

   wireAdd() {
      this.querySelector("#sm-add-go").addEventListener("click", async () => {
         const out = this.querySelector("#sm-add-msg");
         const name = this.querySelector("#sm-add-name").value.trim();
         if (!name) { out.textContent = "Name it first."; return; }
         out.textContent = "Adding...";
         try {
            const parent = Number(this.querySelector("#sm-add-parent").value) || null;
            const res = await api("/skill-map/skills", { method: "POST", body: JSON.stringify({
               name, kind: this.querySelector("#sm-add-kind").value, area: this.querySelector("#sm-add-area").value || null,
               ...(parent ? { parent_id: parent } : {}) }) });
            const d = await res.json();
            if (!d.ok) { out.textContent = `Not added: ${reason(d.reason, d.detail)}.`; return; }
            this.map = await this.get("/skill-map");
            this.renderSkills();                         // counts in the filter follow the new skill
            const again = this.querySelector("#sm-add-msg");
            if (again) again.textContent = `Added ${d.skill.name} (To review).`;
         } catch { out.textContent = "Not added (offline)."; }
      });
   }

   updateCounts() {
      const c = this.map.counts;
      const label = { all: `All (${c.total})`, review: `To review (${c.review})`, foundational: `Foundational (${c.foundational})`,
                      position: `Position (${c.position})` };
      this.querySelectorAll("#sm-show option").forEach((o) => { if (label[o.value]) o.textContent = label[o.value]; });
   }

   visible(s) {
      const f = this.filter;
      if (f.show === "retired") { if (!s.retired) return false; } else if (s.retired) return false;
      if (f.show === "review" && s.scope !== "review") return false;
      if (f.show === "foundational" && s.scope !== "foundational") return false;
      if (f.show === "position" && s.scope !== "position") return false;
      if (f.show === "no_test" && s.coverage.total > 0) return false;
      if (f.show === "now" && s.priority !== "now") return false;
      if (f.pos && !(s.targets[f.pos] && Number(s.targets[f.pos].importance) >= 2) && s.scope !== "foundational") return false;
      if (f.q) {
         const q = f.q.toLowerCase();
         if (![s.name, s.gloss, ...(s.evidence_tags || [])].some((x) => String(x || "").toLowerCase().includes(q))) return false;
      }
      return true;
   }

   drawAreas() {
      const m = this.map;
      const host = this.querySelector("#sm-areas");
      const groups = [...m.areas, null];
      const html = groups.map((a) => {
         // usages sit under their function (2026-10-19), whatever area they carry
         const ids = new Set(m.skills.map((s) => s.skill_id));
         const kids = new Map();
         for (const s of m.skills) if (s.parent_id && ids.has(s.parent_id)) {
            if (!kids.has(s.parent_id)) kids.set(s.parent_id, []);
            kids.get(s.parent_id).push(s);
         }
         const tops = m.skills.filter((s) => (s.area || null) === a && !(s.parent_id && ids.has(s.parent_id)));
         const narrowed = !!(this.filter.q || this.filter.show !== "all" || this.filter.pos);
         const rowsHtml = [];
         let shown = 0;
         for (const top of tops) {
            const ch = (kids.get(top.skill_id) || []).filter((c) => this.visible(c));
            if (!this.visible(top) && !ch.length) continue;
            shown++;
            const open = narrowed || this.open.has(top.skill_id);
            rowsHtml.push(this.row(top, { usages: (kids.get(top.skill_id) || []).length, open }));
            for (const c of ch) rowsHtml.push(this.row(c, { child: top.skill_id, hidden: !open }));
         }
         if (!shown) return "";
         const all = m.skills.filter((s) => (s.area || null) === a && !s.retired && !(s.parent_id && ids.has(s.parent_id)));
         const allKids = all.flatMap((s) => (kids.get(s.skill_id) || []).filter((c) => !c.retired));
         const left = [...all, ...allKids].filter((s) => s.scope === "review").length;
         return `
            <details class="sm-area" open>
               <summary><span class="sm-area-name">${esc(a || "No area yet")}</span>
                  <span class="sm-area-sub">${all.length} skills${allKids.length ? ` and ${allKids.length} usages` : ""}${left ? `, <b>${left} to review</b>` : ", all decided"}</span></summary>
               <div class="sm-scroll"><table class="cd-table sm-table">
                  <thead><tr><th>Skill</th><th>Scope</th><th class="sm-pos">Tier<div class="sm-sub">difficulty</div></th>${POS.map((p) => `<th class="sm-pos">${p}<div class="sm-sub">importance</div></th>`).join("")}
                     <th>Area and evidence</th><th class="sm-cov">Tabs T1-T5</th><th></th></tr></thead>
                  <tbody>${rowsHtml.join("")}</tbody>
               </table></div>
            </details>`;
      }).join("");
      host.innerHTML = html || `<p class="empty">No skills match.</p>`;
      host.querySelectorAll("tr[data-id]").forEach((tr) => this.wireRow(tr));
      host.querySelectorAll(".sm-toggle").forEach((b) => b.addEventListener("click", () => this.toggle(b)));
   }

   /* Show or hide a function's usage rows in place (keeps unsaved edits in any row). */
   toggle(b) {
      const id = Number(b.dataset.id);
      const open = !this.open.has(id);
      if (open) this.open.add(id); else this.open.delete(id);
      this.querySelectorAll(`tr[data-parent="${id}"]`).forEach((tr) => { tr.hidden = !open; });
      b.textContent = `${open ? "▾" : "▸"} ${b.dataset.n} usage${b.dataset.n === "1" ? "" : "s"}`;
   }

   row(s, opt = {}) {
      const ed = this.map.can_edit;
      const t = (p) => s.targets[p] || { importance: 0, target_tier: null };
      const flags = [
         s.gap === "no_test" ? `<span class="sm-flag sm-flag-red" title="Required, but no tab in the library tests it">No tab yet</span>` : "",
         s.gap === "none_at_target" ? `<span class="sm-flag sm-flag-amber" title="No tab at or above the target tier, so a clear cannot prove it">None at target</span>` : "",
         s.incomplete ? `<span class="sm-flag sm-flag-amber">${esc(reason(s.incomplete))}</span>` : "",
         s.cases ? `<span class="sm-flag" title="Cases that need it">${s.cases} case${s.cases === 1 ? "" : "s"}</span>` : "",
      ].join("");
      const cov = s.coverage;
      const covHtml = [1, 2, 3, 4, 5].map((k) => `<span class="sm-c ${cov[k] ? "on" : ""}" title="Tier ${k}: ${cov[k]} tabs">${cov[k] || "·"}</span>`).join("")
         + (cov.unrated ? `<span class="sm-c on sm-unrated" title="${cov.unrated} tabs with no tier yet">+${cov.unrated}</span>` : "");
      const pri = s.priority || "soon";
      const priCell = ed ? `<label class="sm-pri-l" title="Priority: orders the players' gap lists in the launcher">Priority <select class="sm-pri">${Object.entries(PRI)
            .map(([v, l]) => `<option value="${v}" ${pri === v ? "selected" : ""}>${l}</option>`).join("")}</select></label>`
         : (s.priority && s.priority !== "soon" ? `<div class="sm-sub">priority ${esc(PRI[s.priority])}</div>` : "");
      const scopeCell = ed ? `
            <select class="sm-scope">${["review", "foundational", "position"].map((v) => `<option value="${v}" ${s.scope === v ? "selected" : ""}>${SCOPE_LABEL[v]}</option>`).join("")}</select>
            ${s.scope === "review" && s.proposed_scope ? `<button type="button" class="sm-accept" title="Take the proposal from the v1 map">Proposed: ${SCOPE_LABEL[s.proposed_scope]}</button>` : ""}`
         : `<span class="sm-scope-ro sm-s-${s.scope}">${SCOPE_LABEL[s.scope]}</span>${s.scope === "review" && s.proposed_scope ? `<div class="sm-sub">proposed ${SCOPE_LABEL[s.proposed_scope]}</div>` : ""}`;
      // ONE tier per skill (David, 10/20: "the difficulty doesn't depend on the position"); importance stays per position
      const tiers = POS.map((p) => t(p).target_tier).filter((x) => Number.isInteger(x));
      const tier = tiers.length ? Math.max(...tiers) : null;
      const mixed = new Set(tiers).size > 1;
      const tierCell = ed ? `<select class="sm-tier"><option value="">-</option>${[1, 2, 3, 4, 5].map((v) => `<option ${tier === v ? "selected" : ""}>${v}</option>`).join("")}</select>
            ${mixed ? `<div class="sm-sub" title="Saved tiers differ by position (${esc(POS.map((p) => `${p} ${t(p).target_tier ?? "-"}`).join(", "))}); Save sets one tier for all">was ${esc(tiers.join("/"))}</div>` : ""}`
         : `${tier ? `T${tier}` : "-"}${mixed ? `<div class="sm-sub">was ${esc(tiers.join("/"))}</div>` : ""}`;
      const posCell = (p) => ed
         ? `<select class="sm-imp" data-p="${p}">${[0, 1, 2, 3].map((v) => `<option ${Number(t(p).importance) === v ? "selected" : ""}>${v}</option>`).join("")}</select>`
         : `${t(p).importance}`;
      return `
         <tr data-id="${s.skill_id}" ${opt.child ? `data-parent="${opt.child}"` : ""} ${opt.hidden ? "hidden" : ""}
             class="${[s.scope === "review" ? "sm-row-review" : "", opt.child ? "sm-usage-row" : "", opt.usages ? "sm-fn-row" : ""].join(" ").trim()}">
            <td class="sm-skill"><div class="sm-name">${opt.child ? `<span class="sm-usage-mark">↳</span>` : ""}${esc(s.name)}
                  <span class="sm-kind">${esc(s.kind)}</span></div>
               ${s.gloss ? `<div class="sm-gloss">${esc(s.gloss)}</div>` : ""}
               ${s.example ? `<code class="sm-example">${esc(s.example)}</code>` : ""}
               ${opt.usages ? `<div class="sm-fn-note">This row means any use of ${esc(s.name)}; the usages below rate each way of using it.</div>
                  <button type="button" class="sm-toggle" data-id="${s.skill_id}" data-n="${opt.usages}">${opt.open ? "▾" : "▸"} ${opt.usages} usage${opt.usages === 1 ? "" : "s"}</button>` : ""}
               ${flags ? `<div class="sm-flags">${flags}</div>` : ""}</td>
            <td class="sm-scope-cell">${scopeCell}${priCell}${s.priority === "now" ? `<span class="sm-flag sm-flag-red sm-pri-flag">Priority</span>` : ""}</td>
            <td class="sm-pos">${tierCell}</td>
            ${POS.map((p) => `<td class="sm-pos ${(s.tested_for || []).includes(p) ? "sm-tested" : ""}">${posCell(p)}</td>`).join("")}
            <td class="sm-ev">${ed ? `
               <select class="sm-area-sel"><option value="">No area</option>${this.map.areas.map((a) => `<option ${s.area === a ? "selected" : ""}>${esc(a)}</option>`).join("")}</select>
               <input type="text" class="sm-evid" value="${esc((s.evidence_tags || []).join(", "))}" placeholder="tab tags, comma separated">`
               : `<div>${esc((s.evidence_tags || []).join(", ") || "no evidence yet")}</div>`}</td>
            <td class="sm-cov">${covHtml}</td>
            <td class="sm-act">${ed ? `<button type="button" class="sm-save" disabled>Save</button><div class="msg sm-row-msg"></div>` : ""}</td>
         </tr>`;
   }

   wireRow(tr) {
      if (!this.map.can_edit) return;
      const save = tr.querySelector(".sm-save");
      const dirty = () => { save.disabled = false; tr.classList.add("sm-dirty"); };
      tr.querySelectorAll("select, input").forEach((el) => el.addEventListener(el.tagName === "INPUT" ? "input" : "change", dirty));
      const acc = tr.querySelector(".sm-accept");
      if (acc) acc.addEventListener("click", () => {
         const s = this.map.skills.find((x) => String(x.skill_id) === tr.dataset.id);
         tr.querySelector(".sm-scope").value = s.proposed_scope;
         dirty();
      });
      save.addEventListener("click", () => this.saveRow(tr));
   }

   async saveRow(tr) {
      const id = tr.dataset.id;
      const out = tr.querySelector(".sm-row-msg");
      const targets = {};
      for (const p of POS) {
         const tier = tr.querySelector(".sm-tier").value;              // one tier, the same for every position
         targets[p] = { importance: Number(tr.querySelector(`.sm-imp[data-p="${p}"]`).value), target_tier: tier ? Number(tier) : null };
      }
      const body = {
         scope: tr.querySelector(".sm-scope").value, area: tr.querySelector(".sm-area-sel").value || null, targets,
         evidence_tags: tr.querySelector(".sm-evid").value.split(",").map((x) => x.trim()).filter(Boolean),
      };
      const cur = this.map.skills.find((x) => String(x.skill_id) === String(id));
      const pri = tr.querySelector(".sm-pri").value;
      if (pri !== (cur.priority || "soon")) body.priority = pri;      // only when changed (works before the coaching migration)
      out.textContent = "Saving...";
      try {
         const res = await api(`/skill-map/skills/${id}`, { method: "PUT", body: JSON.stringify(body) });
         const d = await res.json();
         if (!d.ok) { out.textContent = reason(d.reason, d.detail); out.className = "msg sm-row-msg err"; return; }
         // reload the map (coverage, flags and counts follow the edit), but redraw ONLY this row and the
         // filter counts, so unsaved changes in other rows survive
         this.map = await this.get("/skill-map");
         const s = this.map.skills.find((x) => String(x.skill_id) === String(id));
         const holder = document.createElement("tbody");
         holder.innerHTML = this.row(s);
         const fresh = holder.firstElementChild;
         tr.replaceWith(fresh);
         this.wireRow(fresh);
         this.updateCounts();
         const again = fresh.querySelector(".sm-row-msg");
         if (again) { again.textContent = "Saved."; again.className = "msg sm-row-msg ok"; }
      } catch { out.textContent = "Not saved (offline)."; out.className = "msg sm-row-msg err"; }
   }

   /* ---------- Requests from the coaches (2026-10-20) ---------- */
   renderRequests(list) {
      const body = this.querySelector("#sm-body");
      const ed = this.map ? this.map.can_edit : null;
      body.innerHTML = `
         <p class="sm-note">What the coaches say the team needs: more tabs for a skill, harder ones, or a skill that is not on the map yet. They send these
            from Team readiness, Coaching. Mark one <b>Being built</b> while you make the practice and <b>Done</b> when the tabs are in a pack;
            your reply shows on their page.</p>
         ${list.length ? `<div class="sm-scroll"><table class="cd-table sm-rq">
            <thead><tr><th>When</th><th>From</th><th>Skill or topic</th><th>What</th><th>Note</th><th>Status and reply</th></tr></thead>
            <tbody>${list.map((r) => `<tr data-rid="${r.request_id}" class="${r.status === "open" ? "sm-rq-open" : ""}">
               <td>${esc(new Date(r.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric" }))}</td>
               <td>${esc(r.created_by_name || "-")}<div class="sm-sub">${esc(r.class_title || "")}</div></td>
               <td><b>${esc(r.skill_name || r.topic)}</b>${r.skill_name ? `<div class="sm-sub">${esc(r.area || "")}</div>` : `<div class="sm-sub">not on the map</div>`}</td>
               <td>${esc(KIND[r.kind] || r.kind)}</td>
               <td class="sm-rq-note">${esc(r.note || "")}</td>
               <td class="sm-rq-act"><select class="sm-rq-status">${Object.entries(RQ).map(([v, l]) => `<option value="${v}" ${r.status === v ? "selected" : ""}>${l}</option>`).join("")}</select>
                  <input type="text" class="sm-rq-reply" maxlength="1000" value="${esc(r.reply || "")}" placeholder="reply (shows on the coach's page)">
                  <button type="button" class="sm-rq-save">Save</button><div class="msg sm-row-msg"></div>
                  ${r.resolved_by_name ? `<div class="sm-sub">closed by ${esc(r.resolved_by_name)}</div>` : ""}</td></tr>`).join("")}</tbody>
         </table></div>` : `<p class="empty">No requests yet.</p>`}`;
      body.querySelectorAll("tr[data-rid]").forEach((tr) => {
         const btn = tr.querySelector(".sm-rq-save");
         if (ed === false) { tr.querySelectorAll("select, input, button").forEach((x) => { x.disabled = true; }); return; }
         btn.addEventListener("click", async () => {
            const out = tr.querySelector(".sm-row-msg");
            out.textContent = "Saving...";
            try {
               const res = await api(`/skill-map/requests/${tr.dataset.rid}`, { method: "PUT",
                  body: JSON.stringify({ status: tr.querySelector(".sm-rq-status").value, reply: tr.querySelector(".sm-rq-reply").value }) });
               const d = await res.json();
               if (!d.ok) { out.textContent = d.reason === "not_authorized" ? "Only David answers requests." : reason(d.reason); out.className = "msg sm-row-msg err"; return; }
               out.textContent = "Saved."; out.className = "msg sm-row-msg ok";
               tr.classList.toggle("sm-rq-open", d.request.status === "open");
               this.requestBadge();
            } catch { out.textContent = "Not saved (offline)."; out.className = "msg sm-row-msg err"; }
         });
      });
   }

   /* ---------- Practice to build ---------- */
   renderGaps(gaps) {
      const body = this.querySelector("#sm-body");
      if (!gaps.length) { body.innerHTML = `<p class="empty">No gaps: every required skill has a tab at or below its target.</p>`; return; }
      const tgt = (s) => { const v = POS.map((p) => s.targets[p] && s.targets[p].target_tier).filter(Boolean); return v.length ? `T${Math.max(...v)}` : ""; };
      body.innerHTML = `
         <p class="sm-note">Skills the team is expected to have, or that a case needs, with no tab to test them (or none at the target tier).
            This is the build list: practice made from the cases that need these, tagged with the skill so it becomes testable.</p>
         <div class="sm-scroll"><table class="cd-table">
            <thead><tr><th>Skill</th><th>Area</th><th>Gap</th><th>Tested for</th><th>Tier</th><th>Cases</th><th class="sm-cov">Tabs T1-T5</th></tr></thead>
            <tbody>${gaps.map((g) => `<tr>
               <td><b>${esc(g.name)}</b> <span class="sm-kind">${esc(g.kind)}</span>${g.scope === "review" ? ` <span class="sm-flag">to review</span>` : ""}</td>
               <td>${esc(g.area || "-")}</td>
               <td>${g.gap === "no_test" ? `<span class="sm-flag sm-flag-red">No tab yet</span>` : `<span class="sm-flag sm-flag-amber">None at target</span>`}</td>
               <td>${esc(g.tested_for.join(", ") || "-")}</td><td>${esc(tgt(g) || "-")}</td>
               <td>${g.cases ? `${g.cases}${g.case_max_tier ? ` (up to T${g.case_max_tier})` : ""}` : "-"}</td>
               <td class="sm-cov">${[1, 2, 3, 4, 5].map((k) => `<span class="sm-c ${g.coverage[k] ? "on" : ""}">${g.coverage[k] || "·"}</span>`).join("")}</td></tr>`).join("")}</tbody>
         </table></div>`;
   }

   /* ---------- Cases ---------- */
   renderCases(cases) {
      const body = this.querySelector("#sm-body");
      body.innerHTML = cases.length ? `
         <p class="sm-note">Each case broken into the skills it cannot be solved without. Open one to see where each skill sits on the map,
            whether practice exists at the tier the case needs, and how your team stands on it.</p>
         <table class="cd-table">
            <thead><tr><th>Case</th><th>Event</th><th>Status</th><th>Skills</th><th>Core</th><th>Points</th></tr></thead>
            <tbody>${cases.map((c) => `<tr><td><a href="#skill-map/case/${encodeURIComponent(c.case_slug)}"><b>${esc(c.title)}</b></a>
               ${c.summary ? `<div class="sm-gloss">${esc(c.summary)}</div>` : ""}</td>
               <td>${esc([c.event, c.division].filter(Boolean).join(", ") || "-")}</td><td>${esc(c.status)}</td>
               <td class="cx-num">${c.skills}</td><td class="cx-num">${c.core}</td><td class="cx-num">${c.points ? c.points.toLocaleString("en-US") : "-"}</td></tr>`).join("")}</tbody>
         </table>` : `<p class="empty">No cases broken down yet. A case breakdown is seeded with server/seed-skill-map.js --cases-only.</p>`;
   }

   async renderCase(slug, classId = this.caseClass || "") {
      const body = this.querySelector("#sm-body");
      if (!this.classes) {
         try {
            const r = await api("/list-classes");
            const d = await r.json();
            this.classes = (Array.isArray(d) ? d : d.classes || []).map((c) => ({ id: c.class_id, title: c.title || c.class_name || `Class ${c.class_id}` }));
         } catch { this.classes = []; }
      }
      const d = await this.get(`/skill-map/cases/${encodeURIComponent(slug)}${classId ? `?class=${classId}` : ""}`);
      const c = d.case;
      const chip = (k) => ({
         no_test: `<span class="sm-flag sm-flag-red">No tab yet</span>`, none_at_tier: `<span class="sm-flag sm-flag-amber">None at this tier</span>`,
         ok: `<span class="sm-flag sm-flag-green">Practice exists</span>`, to_review: `<span class="sm-flag">To review</span>`,
         on_map: "" }[k] || "");
      body.innerHTML = `
         <p><a href="#skill-map/cases">&larr; Cases</a></p>
         <h2 class="sm-case-title">${esc(c.title)}</h2>
         <p class="sm-note">${esc([c.event, c.division, c.status].filter(Boolean).join(" · "))}${c.summary ? `<br>${esc(c.summary)}` : ""}</p>
         <div class="sm-bar"><label>Team <select id="sm-case-class"><option value="">(pick a class to see how the team stands)</option>
            ${this.classes.map((k) => `<option value="${k.id}" ${String(k.id) === String(classId) ? "selected" : ""}>${esc(k.title)}</option>`).join("")}</select></label></div>
         <div class="sm-scroll"><table class="cd-table">
            <thead><tr><th>Skill</th><th>Used where</th><th class="cx-num">Points</th><th>Role</th><th>Owner</th><th>Tier needed</th>
               <th>On the map</th><th>Practice</th>${classId ? "<th>Team (met / expected)</th>" : ""}</tr></thead>
            <tbody>${d.skills.map((s) => `<tr>
               <td><b>${esc(s.name)}</b> <span class="sm-kind">${esc(s.kind || "")}</span>${s.note ? `<div class="sm-gloss">${esc(s.note)}</div>` : ""}</td>
               <td>${esc(s.used_where || "-")}</td><td class="cx-num">${s.points ?? "-"}</td><td>${esc(s.role || "-")}</td>
               <td>${esc(s.lead || "-")}${s.lane ? `<div class="sm-sub">${esc(s.lane)}</div>` : ""}</td>
               <td>${s.tier_needed ? `T${s.tier_needed}` : "-"}</td>
               <td>${esc(s.area || "No area")}<div>${s.map_status === "to_review" ? chip("to_review") : `<span class="sm-sub">${esc(SCOPE_LABEL[s.scope] || "")}${s.tested_for && s.tested_for.length && s.scope === "position" ? `: ${esc(s.tested_for.join(", "))}` : ""}</span>`}</div></td>
               <td>${chip(s.practice_status)}</td>
               ${classId ? `<td>${s.team ? `${s.team.met} / ${s.team.expected}` : `<span class="sm-sub">not tested yet</span>`}</td>` : ""}</tr>`).join("")}</tbody>
         </table></div>`;
      body.querySelector("#sm-case-class").addEventListener("change", (e) => {
         this.caseClass = e.target.value;
         this.renderCase(slug, e.target.value).catch((err) => this.msg(`Could not load (${err.message}).`, "err"));
      });
   }
}

customElements.define("app-skill-map", AppSkillMap);
